// Bedien-Cockpit (read-only, ADR-030 Phase 0). Übersicht + Prüf-Queue +
// Datensätze + Bausteine-Katalog — alles nur Lesen. Bearbeiten/Bindings/
// Bestätigen kommen in Phase 1/2. Tenant-scoped über den eingeloggten User;
// ein tenant-übergreifender Super-Admin folgt später.

import type { ReactNode } from "react";

import {
  useAdminOverview,
  useBausteine,
  useDatasets,
  useReviewQueue,
} from "@/api/hooks";
import { Loader } from "@/components/Loader";

import "./AdminPage.css";

export function AdminPage() {
  return (
    <div className="cockpit">
      <h1 className="cockpit-title">Cockpit</h1>
      <p className="bmd-muted cockpit-sub">
        Referenzdaten, Bausteine und Prüf-Queue — read-only (ADR-030 Phase 0).
      </p>
      <OverviewSection />
      <ReviewSection />
      <DatasetsSection />
      <BausteineSection />
    </div>
  );
}

function OverviewSection() {
  const q = useAdminOverview();
  if (q.isLoading) return <Loader label="Übersicht…" />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const o = q.data!;
  const cards = [
    { label: "Mandanten", value: o.mandanten },
    { label: "Datensätze", value: o.datasets },
    { label: "Aktive Workflows", value: o.active_workflows },
    { label: "Prüfung offen", value: o.review_pending },
  ];
  return (
    <section className="cockpit-cards">
      {cards.map((c) => (
        <div key={c.label} className="cockpit-card">
          <span className="cockpit-card-value">{c.value}</span>
          <span className="cockpit-card-label">{c.label}</span>
        </div>
      ))}
    </section>
  );
}

function ReviewSection() {
  const q = useReviewQueue();
  return (
    <Section title="Prüfung (Confidence-Gate)">
      {q.isLoading ? (
        <Loader label="Prüf-Queue…" />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data!.length === 0 ? (
        <p className="bmd-muted">Keine Belege in Prüfung.</p>
      ) : (
        <table className="cockpit-table">
          <thead>
            <tr>
              <th>Datei</th>
              <th>Quelle</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {q.data!.map((b) => (
              <tr key={b.id}>
                <td>{b.original_filename}</td>
                <td>{b.mandant_source}</td>
                <td>{b.mandant_confidence ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function DatasetsSection() {
  const q = useDatasets();
  return (
    <Section title="Datensätze">
      {q.isLoading ? (
        <Loader label="Datensätze…" />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data!.length === 0 ? (
        <p className="bmd-muted">Noch keine Listen importiert.</p>
      ) : (
        <table className="cockpit-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Schlüssel</th>
              <th>Spalten</th>
              <th>Zeilen</th>
            </tr>
          </thead>
          <tbody>
            {q.data!.map((d) => (
              <tr key={d.id}>
                <td>{d.name}</td>
                <td>{d.key_column}</td>
                <td>{d.columns.map((c) => c.label).join(", ")}</td>
                <td>{d.row_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function BausteineSection() {
  const q = useBausteine();
  return (
    <Section title="Bausteine (Library)">
      {q.isLoading ? (
        <Loader label="Bausteine…" />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : (
        <table className="cockpit-table">
          <thead>
            <tr>
              <th>Aktion</th>
              <th>Beschreibung</th>
              <th>liest → schreibt</th>
            </tr>
          </thead>
          <tbody>
            {q.data!.map((b) => (
              <tr key={b.action}>
                <td>
                  <code>{b.action}</code>
                </td>
                <td>{b.beschreibung}</td>
                <td className="bmd-muted">
                  {b.liest.join(", ") || "—"} → {b.schreibt.join(", ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bmd-card cockpit-section">
      <h2 className="cockpit-section-title">{title}</h2>
      {children}
    </section>
  );
}

function ErrorBox({ error }: { error: Error }) {
  return <p className="cockpit-error">Fehler: {error.message}</p>;
}
